import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvironmentActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';
import { Base64 } from '@app/shared/helpers/base64';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { SigninActions } from '../actions';
import * as fromSignin from '../reducers';
import { InviteActions } from '../actions';
import { isInThePast } from 'smash-time';
import { Iam, errors as iamErrors } from '@smash-sdk/iam/ejs/07-2021';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Constant } from 'src/constant';
import AcceptInvitationError from '@smash-sdk/iam/ejs/07-2021/types/AcceptInvitation/AcceptInvitationError';


@Injectable()
export class InviteEffects {
    decodeMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InviteActions.decodeMessage),
            switchMap((action) => {
                try {
                    const base64 = new Base64();
                    const decodedMessage = base64.decode(action.message);
                    const decodedMessageToObject = JSON.parse(decodedMessage);
                    if (isInThePast(decodedMessageToObject.expiry)) {
                        return of(InviteActions.inviteTokenExpired({ error: 'INVITE_TOKEN_EXPIRED_BODY' }))
                    }
                    return [
                        EnvironmentActions.loadServices({ region: JSON.parse(decodedMessage).region }),
                        InviteActions.decodeMessageSuccess({ decodedMessage: JSON.parse(decodedMessage) })
                    ];
                } catch (error) {
                    return of(InviteActions.decodeMessageFailure({ error: error.message }))
                }
            })
        )
    );

    acceptInvitation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InviteActions.acceptInvitation),
            withLatestFrom(
                this.store$.select(fromSignin.getInviteDecodedMessage)
            ),
            switchMap(([action, decodedMessage]: any[]) => {
                const iamSdk = new Iam();
                return defer(() => iamSdk.acceptInvitation({ ...decodedMessage, password: action.password })).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: AcceptInvitationError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: AcceptInvitationError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: AcceptInvitationError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    map(() => {
                        const user = { password: action.password, username: decodedMessage.username };
                        return InviteActions.acceptInvitationSuccess({ user });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error instanceof iamErrors.AcceptInvitationError.ExpiredInvitationError) {
                            return of(InviteActions.inviteTokenExpired({ error: 'INVITE_TOKEN_EXPIRED_BODY' }));
                        } else if (error instanceof iamErrors.AcceptInvitationError.InvalidInvitationError) {
                            return of(InviteActions.acceptInvitationFailure({ error: 'INVITE_TOKEN_INVALID_BODY' }));
                        } else if (error instanceof iamErrors.AcceptInvitationError.InvalidInvitationSecretError) {
                            return of(InviteActions.acceptInvitationFailure({ error: 'INVITE_TOKEN_INVALID_BODY' }));
                        } else if (error instanceof iamErrors.AcceptInvitationError.NotFoundError) {
                            return of(InviteActions.acceptInvitationFailure({ error: 'INVITE_USER_NOT_FOUND_ERROR' }));
                        } else {
                            return of(InviteActions.acceptInvitationFailure({ error: error.error.error }));
                        }
                    })
                )
            })
        )
    );

    acceptInvitationSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InviteActions.acceptInvitationSuccess),
            map(action => action.user),
            concatMap((user) => {
                return [SigninActions.signinWithPassword({ username: user.username, password: user.password })];
            })
        )
    );


    constructor(
        private actions$: Actions,
        private store$: Store<fromSignin.State & fromRoot.State>
    ) { }

}
